import React, { useEffect, useState } from 'react';
import { Autocomplete, Checkbox, Container, Grid, TextField, FormControlLabel, InputAdornment, Stack } from '@material-ui/core';
import { LoadingButton } from '@material-ui/lab';
import Page from '../../../../components/Page';
import Snackbar from '../../../../components/Snackbar';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';

import { useTranslation } from 'react-i18next';
import { RootState, useDispatch, useSelector } from '../../../../redux/store';
import { createItem, getFactoryOptions, resetMessage, updateItem } from '../store/itemSlice';
import { useSnackbar } from 'notistack5';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, SubmitHandler, Controller, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { DevTool } from '@hookform/devtools';

import { Factory, ItemUnit } from '../../../..//@types/common';
import { ITEM_UNIT } from '../../../../constants';
import { itemAPI } from '../api/itemAPI';

interface IFormInputs {
  code?: string;
  showaItemCode?: string;
  nameEnglish?: string;
  nameJapanese?: string;
  handledFactory?: Factory[];
  orderableUnit?: any;
  displayOrder?: number;
  weightPerBag?: number;
  bagsPerFcl?: number;
  isDeleted?: boolean;
}


const ItemCreateEdit: React.FC = (): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { onSnackbarClose } = Snackbar();
  const { hasError, message, isLoading, options } = useSelector((state: RootState) => state.item);
  const navigate = useNavigate();
  const { id } = useParams();
  const [factoryInputValue, setFactoryInputValue] = useState('');
  const [unitInputValue, setUnitInputValue] = useState('');

  const isEditMode: boolean = id !== undefined ? true : false;
  const factory: Factory[] = options.factory;


  // サンプル：メッセージをカスタマイズする場合は以下の様に書く
  yup.setLocale({
    string: { min: '最低文字列は ${min} です', max: '最大文字列は${max}です' },
  });

  /**
   * ここでフォームのバリデーション仕様を定義する
   */
  const schema = yup
    .object({
      handledFactory: yup.array().test('Not Empty Array', 'handledFactory is a required field', (value, context) => {
        if (value && value.length > 0) {
          return true;
        } else {
          return false;
        }
      }),
      code: yup.string().required('入力必須です').min(3).max(50),
      showaItemCode: yup.string().required().min(4).max(10),
      nameEnglish: yup.string().required().max(50),
      nameJapanese: yup.string().required().max(50),
      bagsPerFcl: yup.number(),
      weightPerBag: yup.number(),
      displayOrder: yup.number(),
    })
    .required();

  type OrderableUnit = { label: string; value: ItemUnit };
  const orderableOptions: OrderableUnit[] = [
    { label: 'Bag', value: ITEM_UNIT.BAG },
    { label: 'FCL', value: ITEM_UNIT.FCL },
  ];

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<IFormInputs>({
    resolver: yupResolver(schema) as Resolver<IFormInputs>,
  });

  /**
   * itemを取得
   * @param itemCode
   */
  const getItemData = async (itemCode: string) => {
    const item = await itemAPI.getItem(itemCode);
    // console.log('🍌', item);
    setValue('code', item.code);
    setValue('showaItemCode', item.showaItemCode ?? '');
    setValue('nameJapanese', item.nameJapanese ?? '');
    setValue('nameEnglish', item.nameEnglish ?? '');
    setValue('displayOrder', item.displayOrder ?? undefined);
    setValue('bagsPerFcl', item.bagsPerFcl ?? undefined);
    setValue('weightPerBag', item.weightPerBag ?? undefined);
    setValue('isDeleted', item.isDeleted);

    // state上ではobjectとして管理する
    const selectedFactories: Factory[] = factory?.filter((factory: Factory) => item.handledFactoryCode?.includes(factory.code));
    setValue('handledFactory', selectedFactories);

    const selectedOrderableUnit: OrderableUnit[] = orderableOptions?.filter((orderableUnit: OrderableUnit) =>
      item.orderableUnit?.includes(orderableUnit.value),
    );
    setValue('orderableUnit', selectedOrderableUnit);
  };

  // side effect
  // --------------------------------------------------

  useEffect(() => {
    // ここで工場の一覧を取得する
    dispatch(getFactoryOptions());
  }, []);

  useEffect(() => {
    if (id && factory.length) {
      // ここでItemを一件取得する
      getItemData(id);
    }
  }, [factory]);

  useEffect(() => {
    if (hasError && message) {
      enqueueSnackbar(message, { variant: 'error', persist: true });
      dispatch(resetMessage());
    } else if (!hasError && message) {
      enqueueSnackbar(message, { variant: 'success' });
      dispatch(resetMessage());
    }
  }, [hasError, message, enqueueSnackbar, dispatch]);

  /**
   * フォーム送信
   * @param data
   */
  const formSubmitHandler: SubmitHandler<IFormInputs> = async (data) => {
    // console.log('🐊form data is ', data);
    // console.log('ここでデータを送信します。');

    const item = {
      ...data,
      orderableUnit: data.orderableUnit?.map((orderableUnit: OrderableUnit) => orderableUnit.value),
      handledFactoryCode: data.handledFactory?.map((factory: Factory) => {
        return factory.code;
      }),
    };
    delete item.handledFactory;

    // console.log('🐊🐊🐊send form data is ', item);
    try {
      if (isEditMode) {
        //データの更新
        // @ts-ignore
        await dispatch(updateItem(item));
      } else {
        // データの登録
        // @ts-ignore
        await dispatch(createItem(item));
      }
      // エラーがなかった場合はページを遷移
      navigate('../../item', { replace: true });
    } catch (error: any) {
      // console.log('🐸', error.response.data);
      // エラーが発生した場合はメッセージを表示
      onSnackbarClose({
        color: 'error',
        msg: error.response.data.message,
      });
      dispatch(resetMessage());
    }
  };

  // view
  // --------------------------------------------------
  return (
    <Page title={isEditMode ? t('masterItemList.edit.title') : t('masterItemList.create.title')}>
      {/* これで最大幅の上限がなくなる */}
      <Container maxWidth={false}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <HeaderBreadcrumbs
              heading={isEditMode ? t('masterItemList.edit.heading') : t('masterItemList.create.heading')}
              links={[
                { name: t('features.master'), href: '/master' },
                { name: t('masterItemList.title'), href: '/master/item' },
                { name: isEditMode ? t('masterItemList.edit.title') : t('masterItemList.create.title')
              }]}
            />
          </Grid>
        </Grid>

        <form onSubmit={handleSubmit(formSubmitHandler)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Controller
                name="code"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="text"
                    label={t('masterItemList.gridHeader.code')}
                    size="small"
                    disabled={isLoading || isEditMode}
                    variant="outlined"
                    error={!!errors.code}
                    helperText={errors.code ? errors.code?.message : ''}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}></Grid>
            <Grid item xs={12} sm={6} md={8}>
              <Controller
                name="showaItemCode"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="text"
                    label={t('masterItemList.gridHeader.showaItemCode')}
                    size="small"
                    disabled={isLoading}
                    variant="outlined"
                    error={!!errors.showaItemCode}
                    helperText={errors.showaItemCode ? errors.showaItemCode?.message : ''}
                  />
                )}
              />
            </Grid>{' '}
            <Grid item xs={12} sm={6} md={4}></Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Controller
                name="nameJapanese"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="text"
                    disabled={isLoading}
                    label={t('masterItemList.gridHeader.nameJapanese')}
                    size="small"
                    variant="outlined"
                    error={!!errors.nameJapanese}
                    helperText={errors.nameJapanese ? errors.nameJapanese?.message : ''}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Controller
                name="nameEnglish"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    type="text"
                    disabled={isLoading}
                    label={t('masterItemList.gridHeader.nameEnglish')}
                    size="small"
                    variant="outlined"
                    error={!!errors.nameEnglish}
                    helperText={errors.nameEnglish ? errors.nameEnglish?.message : ''}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Controller
                name="handledFactory"
                control={control}
                render={({ field, formState }) => (
                  <Autocomplete
                    {...field}
                    multiple
                    filterSelectedOptions
                    size="small"
                    disabled={isLoading}
                    options={factory}
                    defaultValue={getValues(field.name) ?? []}
                    value={getValues(field.name) ?? []}
                    onChange={(e, selectedOptions) => {
                      setValue('handledFactory', selectedOptions);
                    }}
                    inputValue={factoryInputValue}
                    onInputChange={(_, newInputValue) => {
                      setFactoryInputValue(newInputValue);
                    }}
                    getOptionLabel={(option: Factory) => option.nameEnglish}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{ maxWidth: '400px' }}
                        label={t('masterItemList.gridHeader.handledFactoryCode')}
                        error={!!errors.handledFactory}
                        // @ts-ignore
                        helperText={errors.handledFactory?.message ?? ''}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Controller
                name="orderableUnit"
                control={control}
                render={({ field, formState }) => (
                  <Autocomplete
                    {...field}
                    multiple
                    filterSelectedOptions
                    size="small"
                    disabled={isLoading}
                    options={orderableOptions}
                    defaultValue={getValues(field.name) ?? []}
                    value={getValues(field.name) ?? []}
                    onChange={(e, options: OrderableUnit[]) => {
                      setValue('orderableUnit', options);
                    }}
                    inputValue={unitInputValue}
                    onInputChange={(_, newInputValue) => {
                      setUnitInputValue(newInputValue);
                    }}
                    getOptionLabel={(option: OrderableUnit) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{ maxWidth: '400px' }}
                        label={t('masterItemList.gridHeader.orderableUnit')}
                        name="orderableUnit"
                        error={!!errors.orderableUnit}
                        helperText={errors.orderableUnit ? errors.orderableUnit?.message : ''}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}></Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Stack spacing={2}>
                <Controller
                  name="weightPerBag"
                  control={control}
                  defaultValue={0}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="number"
                      label={t('masterItemList.gridHeader.weightPerBag')}
                      size="small"
                      disabled={isLoading}
                      variant="outlined"
                      error={!!errors.weightPerBag}
                      helperText={errors.weightPerBag ? errors.weightPerBag?.message : ''}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">kg/bag</InputAdornment>,
                      }}
                    />
                  )}
                />
                <Controller
                  name="bagsPerFcl"
                  control={control}
                  defaultValue={0}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('masterItemList.gridHeader.bagsPerFcl')}
                      size="small"
                      disabled={isLoading}
                      variant="outlined"
                      error={!!errors.bagsPerFcl}
                      helperText={errors.bagsPerFcl ? errors.bagsPerFcl?.message : ''}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">bag/fcl</InputAdornment>,
                      }}
                      type="number"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                />{' '}
                <Controller
                  name="displayOrder"
                  control={control}
                  defaultValue={0}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="number"
                      label={t('masterItemList.gridHeader.displayOrder')}
                      size="small"
                      disabled={isLoading}
                      variant="outlined"
                      error={!!errors.displayOrder}
                      helperText={errors.displayOrder ? errors.displayOrder?.message : ''}
                    />
                  )}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={8} md={9}></Grid>
            <Grid item xs={12} md={5}>
              <Controller
                name="isDeleted"
                defaultValue={false}
                control={control}
                render={({ field }) => (
                  <section>
                    <FormControlLabel
                      value="end"
                      control={<Checkbox disabled={isLoading} onChange={(e) => field.onChange(e.target.checked)} checked={field.value} />}
                      label={t('masterItemList.gridHeader.isDeleted')}
                      labelPlacement="end"
                    />
                  </section>
                )}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <Controller
                name="isDeleted"
                defaultValue={false}
                control={control}
                render={({ field }) => (
                  <LoadingButton variant="contained" type="submit" loading={isLoading}>
                    {t('button.save')}
                  </LoadingButton>
                )}
              />
            </Grid>
          </Grid>
          {/* <DevTool control={control} /> */}
        </form>
      </Container>
    </Page>
  );
};

export default ItemCreateEdit;
